// The verse in the translations we hold in full, and the switch that puts one
// of them in a column beside the ESV for the whole chapter.
//
// The choice lives in reader settings, so it follows the member between
// devices and survives a reload the way font size does.
import { translations, type ReaderSettings, type TranslationId } from "@bibleunleashed/shared";
import { track } from "@/lib/events";
import { useChapterText, useTranslations } from "@/lib/study";
import { RailCard } from "./RailCard";

/** One verse of one translation. Cheap: the whole chapter is already cached. */
function VerseIn({ translation, verse }: { translation: TranslationId; verse: string }) {
  const { data } = useChapterText(translation, verse);
  const text = data?.verses[0]?.text;
  return (
    <div>
      <p className="font-sans text-[10.5px] tracking-[0.08em] text-ink-3 uppercase">{translation}</p>
      <p className="mt-0.5 font-serif text-[14.5px] leading-snug text-ink-2">{text ?? "…"}</p>
    </div>
  );
}

export function TranslationsCard({
  verse,
  display,
  parallel,
  onParallel,
}: {
  verse: string | null;
  display?: string;
  parallel: ReaderSettings["parallel"];
  onParallel: (value: ReaderSettings["parallel"]) => void;
}) {
  const { data: available } = useTranslations();
  const usable = (available ?? []).filter((t) => t.available);
  const ids = usable.length ? usable.map((t) => t.id) : ([] as TranslationId[]);

  return (
    <RailCard title="Other translations" verse={display}>
      {!ids.length ? (
        <p className="font-serif text-[14.5px] leading-relaxed text-ink-3 italic">
          No public-domain text is loaded yet — run <code className="not-italic">scripts/import-opentext.mjs</code>.
        </p>
      ) : (
        <>
          <div className="mb-3 flex flex-wrap gap-1.5">
            {translations
              .filter((id) => ids.includes(id))
              .map((id) => {
                const on = parallel === id;
                return (
                  <button
                    key={id}
                    onClick={() => {
                      track({ type: "study_translations", ref: verse ?? undefined, detail: on ? `hide ${id}` : id });
                      onParallel(on ? "none" : id);
                    }}
                    aria-pressed={on}
                    title={on ? `Stop showing ${id} beside the ESV` : `Show ${id} beside the ESV`}
                    className={`rounded border px-1.5 py-0.5 font-sans text-[11px] ${
                      on ? "border-accent bg-accent-soft text-accent" : "border-rule text-ink-3 hover:text-ink"
                    }`}
                  >
                    {id}
                  </button>
                );
              })}
            <span className="self-center font-sans text-[10.5px] text-ink-3">
              {parallel === "none" ? "beside the text" : "showing beside the text"}
            </span>
          </div>
          {verse ? (
            <div className="space-y-3">
              {ids.map((id) => (
                <VerseIn key={id} translation={id} verse={verse} />
              ))}
            </div>
          ) : (
            <p className="font-serif text-[14.5px] leading-relaxed text-ink-3 italic">
              Click a verse to compare it.
            </p>
          )}
        </>
      )}
    </RailCard>
  );
}
